import { forwardRef, useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import {
  bookingDurationAtom,
  isBookingMultiDayAtom,
  selectedBookingPackageAtom,
  bookingTripTimeAtom,
} from 'components/forms/bookingWidget/jotaiStore';
import { useTripCalculator } from 'hooks/queries';
import PriceExclFees from 'components/forms/bookingWidget/PriceExclFees';
import NotChargedYet from 'components/forms/bookingWidget/NotChargedYet';
import BookingForm from './BookingForm';
import { useBoatDetails } from '../../pdp/v3/hooks';
import OutstandingOffer from './OutstandingOffer';
import BookingTerms from './BookingTerms';
import CancellationInfo from './CancellationInfo';

interface Props {
  boatId: string;
  primaryPackage: object;
  isMessageOwner?: boolean;
}

const BookingWidget = forwardRef<HTMLDivElement, Props>(
  ({ boatId, primaryPackage, isMessageOwner = false }: Props, ref) => {
    const { boatDetails } = useBoatDetails(boatId);
    const { tripCalculation, isLoading, isIdle } = useTripCalculator();
    const tripTime = useAtomValue(bookingTripTimeAtom);
    const isBookingMultiDay = useAtomValue(isBookingMultiDayAtom);
    const bookingDuration = useAtomValue(bookingDurationAtom);
    const selectedBookingPackage = useAtomValue(selectedBookingPackageAtom);
    // If the booking start time has been selected, it allows enough information for an extended view.
    const [isExpanded, setExpanded] = useState(!!tripTime);

    // If the days remaining on free_cancelation_until are greater than 0, free cancelation exists.
    const hasFreeCancellation =
      tripCalculation?.free_cancelation_until &&
      dayjs(tripCalculation.free_cancelation_until).diff(dayjs(), 'day') !== 0;

    return (
      <StyledBookingWidget ref={ref}>
        {/* This warns the user if there is already a booking or inquiry for this boat. */}
        <OutstandingOffer boatId={boatId} />
        <PriceExclFees
          primaryPackage={primaryPackage}
          isLoading={isLoading}
          isIdle={isIdle}
          tripCalculation={tripCalculation}
          isBookingMultiDay={isBookingMultiDay}
          bookingDuration={bookingDuration}
        />
        {/*
          BookingForm contains the following:
          - Date input
          - Duration input
          - Start time input
          - Captain option input (if start time set)
          - Price breakdown
            - Boat price and captain price (if the start time is set)
            - Booking total with fees (if start time set)
          - CTA
        */}
        <BookingForm
          boatId={boatId}
          isMessageOwner={isMessageOwner}
          setExpanded={setExpanded}
          isExpanded={isExpanded}
          primaryPackage={primaryPackage}
        />
        {/* Terms */}
        {isExpanded && (
          <BookingTerms
            boatId={boatId}
            securityDepositCents={selectedBookingPackage?.security_deposit_cents}
            cancellationPolicy={boatDetails.cancellation_policy}
            fuelPolicy={selectedBookingPackage?.fuel_policy}
          />
        )}
        {/* Free cancellation text */}
        {hasFreeCancellation && <CancellationInfo />}
        {/* Charge copy */}
        <NotChargedYet />
      </StyledBookingWidget>
    );
  }
);

export default BookingWidget;

const StyledBookingWidget = styled.div`
  padding: 35px;
  width: 400px;
  height: fit-content;
  box-shadow: 0px 0px 33px rgba(0, 0, 0, 0.11);
  border-radius: 15px;
  margin: 0 auto;
  background: white;
  position: sticky;
  top: 50px;

  margin-top: -60px;
  z-index: 1;

  isolation: isolate;
`;
